import { useEffect, useState } from "react";
import { testimonials, businessInfo } from "@/mocks/home";

export default function Testimonials() {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (paused) return;
    const timer = setInterval(() => {
      setIndex((prev) => (prev + 1) % testimonials.length);
    }, 6000);
    return () => clearInterval(timer);
  }, [paused]);

  const goPrev = () => setIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  const goNext = () => setIndex((prev) => (prev + 1) % testimonials.length);
  const current = testimonials[index];

  return (
    <section id="testimonios" className="w-full bg-background-950 relative overflow-hidden">
      <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-primary-500/10"></div>
      <div className="absolute -bottom-32 -left-20 w-80 h-80 rounded-full bg-accent-500/10"></div>

      <div className="relative w-full px-4 md:px-8 py-16 md:py-24">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-10 lg:gap-14 items-center">
            <div className="lg:col-span-2">
              <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary-500/20 text-primary-200 text-xs font-semibold uppercase tracking-wider mb-5">
                <i className="ri-chat-smile-3-line"></i>
                Testimonios
              </span>
              <h2 className="font-heading text-3xl md:text-5xl font-semibold text-background-50 leading-tight tracking-tight">
                Lo que dicen nuestros pacientes
              </h2>
              <p className="mt-5 text-base md:text-lg text-background-300 leading-relaxed">
                Nuestra mejor carta de presentación son las personas que ya confiaron en nosotros. El trato
                humano, la paciencia y la limpieza son lo que más nos recomiendan.
              </p>

              <div className="mt-8 bg-background-900 rounded-lg p-6 md:p-7 border border-background-800">
                <div className="flex items-end gap-4">
                  <span className="font-heading text-5xl md:text-6xl font-semibold text-background-50 leading-none">
                    {businessInfo.rating}
                  </span>
                  <div className="pb-1">
                    <div className="flex items-center gap-1 text-accent-400 mb-1">
                      {[0, 1, 2, 3, 4].map((i) => (
                        <i key={i} className="ri-star-fill text-lg"></i>
                      ))}
                    </div>
                    <p className="text-xs md:text-sm text-background-300">
                      Calificación en Google basada en reseñas reales
                    </p>
                  </div>
                </div>
                <a
                  href={businessInfo.mapsLink}
                  target="_blank"
                  rel="noreferrer"
                  className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-primary-300 hover:text-primary-200 transition-colors cursor-pointer"
                >
                  <i className="ri-google-fill"></i>
                  Ver reseñas en Google
                  <i className="ri-arrow-right-line"></i>
                </a>
              </div>
            </div>

            <div className="lg:col-span-3">
              <div
                className="bg-background-50 rounded-lg p-7 md:p-10 relative"
                onMouseEnter={() => setPaused(true)}
                onMouseLeave={() => setPaused(false)}
              >
                <i className="ri-double-quotes-l text-5xl md:text-6xl text-primary-200 absolute top-6 right-8"></i>

                <div className="flex items-center gap-1 text-accent-500 mb-6">
                  {Array.from({ length: current.rating }).map((_, i) => (
                    <i key={i} className="ri-star-fill text-lg"></i>
                  ))}
                </div>

                <p className="font-heading text-lg md:text-2xl text-foreground-900 leading-relaxed min-h-[168px] md:min-h-[150px]">
                  {current.text}
                </p>

                <div className="mt-8 flex items-center justify-between gap-4 pt-6 border-t border-background-200">
                  <div className="flex items-center gap-4">
                    <span className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-500 text-background-50 font-semibold text-sm flex-shrink-0">
                      {current.initials}
                    </span>
                    <div>
                      <p className="font-semibold text-foreground-950 text-sm md:text-base leading-tight">
                        {current.name}
                      </p>
                      <p className="text-xs md:text-sm text-foreground-500 mt-0.5">{current.role}</p>
                    </div>
                  </div>

                  <div className="hidden sm:flex items-center gap-2">
                    <button
                      type="button"
                      onClick={goPrev}
                      aria-label="Reseña anterior"
                      className="w-10 h-10 flex items-center justify-center rounded-full border border-background-300 text-foreground-700 hover:bg-primary-500 hover:text-background-50 hover:border-primary-500 transition-colors cursor-pointer"
                    >
                      <i className="ri-arrow-left-line"></i>
                    </button>
                    <button
                      type="button"
                      onClick={goNext}
                      aria-label="Siguiente reseña"
                      className="w-10 h-10 flex items-center justify-center rounded-full border border-background-300 text-foreground-700 hover:bg-primary-500 hover:text-background-50 hover:border-primary-500 transition-colors cursor-pointer"
                    >
                      <i className="ri-arrow-right-line"></i>
                    </button>
                  </div>
                </div>

                <div className="mt-6 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {testimonials.map((item, i) => (
                      <button
                        key={item.name}
                        type="button"
                        aria-label={`Ir a la reseña ${i + 1}`}
                        onClick={() => setIndex(i)}
                        className={`h-2 rounded-full transition-all duration-300 cursor-pointer ${
                          i === index ? "w-7 bg-primary-500" : "w-2 bg-background-300 hover:bg-background-400"
                        }`}
                      ></button>
                    ))}
                  </div>
                  <div className="flex sm:hidden items-center gap-2">
                    <button
                      type="button"
                      onClick={goPrev}
                      aria-label="Reseña anterior"
                      className="w-9 h-9 flex items-center justify-center rounded-full border border-background-300 text-foreground-700 cursor-pointer"
                    >
                      <i className="ri-arrow-left-line"></i>
                    </button>
                    <button
                      type="button"
                      onClick={goNext}
                      aria-label="Siguiente reseña"
                      className="w-9 h-9 flex items-center justify-center rounded-full border border-background-300 text-foreground-700 cursor-pointer"
                    >
                      <i className="ri-arrow-right-line"></i>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}